import React, { useEffect, useRef, useState } from 'react';

import TeamsIcon from 'icons/icon-teams.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import PanelIcon from 'components/application/PanelIcon';
import SidePanel from 'components/application/SidePanel';

import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';
import TeamsForm from 'components/teams/TeamsForm';
import TeamSidePanelShow from 'components/teams/TeamSidePanelShow';

import { focusField } from 'components/helpers/refs';

const defaultMembers = {
  loaded: false,
  collection: [],
  meta: { currentPage: 1, isLastPage: false },
};

export default function TeamsSidePanel({
  closeCallback,
  onCreateClick,
  onDeleteClick,
  onEditClick,
  onTeamCompanyOptionChange,
  onTeamMemberDelete,
  onTeamMemberFetch,
  onTeamMemberSelect,
  onTeamMemberStatusChange,
  onTeamNameInputChange,
  onUpdateClick,
  removeErrorStyling,
  requestError,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
  team,
  teamCompanyName,
}) {
  const breadBoard = useBreadBoard();
  const sidePanelFieldRef = useRef();

  const [isFetchingTeamMembers, setIsFetchingTeamMembers] = useState(false);
  const [isLoadMoreMembersActive, setIsLoadMoreMembersActive] = useState(false);
  const [scrolledAmount, setScrolledAmount] = useState(0);
  const [teamMembers, setTeamMembers] = useState(defaultMembers);

  const { hasTeamEditableAccess } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'teams.TeamsSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  function fetchTeamMembers({ page }) {
    setIsLoadMoreMembersActive(false);
    setIsFetchingTeamMembers(true);
    axios
      .get(`/teams/${team.id}/members`, { params: { page } })
      .then((response) => {
        setTeamMembers((prevMembers) => ({
          loaded: true,
          collection:
            page === 'all' ?
              response.data.data
            : prevMembers.collection.concat(response.data.data),
          meta: response.data.meta,
        }));
        onTeamMemberFetch({ members: response.data.data, replace: true });
        setIsLoadMoreMembersActive(true);
        setIsFetchingTeamMembers(false);
      })
      .catch(breadBoard.addInedibleToast)
      .finally(() => setIsFetchingTeamMembers(false));
  }

  function handleEditClick() {
    fetchTeamMembers({ page: 'all' });
    onEditClick();
  }

  function onBackdropClick() {
    const panelLocked = ['new', 'edit'].includes(sidePanelContext);
    if (!panelLocked) {
      closeCallback();
    }
  }

  function handleLoadMoreMembers(inView) {
    if (inView)
      fetchTeamMembers({
        page: parseInt(teamMembers.meta.currentPage, 10) + 1,
      });
  }

  useEffect(() => {
    if (team.id && sidePanelIsOpen) {
      fetchTeamMembers({ page: parseInt(teamMembers.meta.currentPage, 10) });
    }

    if (!sidePanelIsOpen) {
      setTeamMembers(defaultMembers);
      setIsLoadMoreMembersActive(false);
    }
  }, [sidePanelIsOpen]);

  const footerContent = (() => {
    switch (sidePanelContext) {
      case 'new':
        return (
          <FormFooter
            onCancel={closeCallback}
            onSubmit={onCreateClick}
            submitButtonDisabled={submitDisabled}
            text={i18nT('new.footer.text')}
          />
        );
      case 'show':
        return hasTeamEditableAccess ?
            <ShowFooter
              deleteText={i18nTCommon('teams.deleteTeam')}
              editText={i18nTCommon('teams.editTeam')}
              onDeleteClick={onDeleteClick}
              onEditClick={handleEditClick}
            />
          : null;
      case 'edit':
        return (
          <FormFooter
            onCancel={closeCallback}
            onSubmit={onUpdateClick}
            submitButtonDisabled={submitDisabled}
            text={i18nTCommon('shared.saveChanges')}
          />
        );
    }
  })();

  const formBodyContent = sidePanelIsOpen && (
    <TeamsForm
      isFetchingTeamMembers={isFetchingTeamMembers}
      onTeamCompanyOptionChange={onTeamCompanyOptionChange}
      onTeamMemberDelete={onTeamMemberDelete}
      onTeamMemberSelect={onTeamMemberSelect}
      onTeamMemberStatusChange={onTeamMemberStatusChange}
      onTeamNameInputChange={onTeamNameInputChange}
      removeErrorStyling={removeErrorStyling}
      requestError={requestError}
      sidePanelContext={sidePanelContext}
      sidePanelFieldRef={sidePanelFieldRef}
      team={team}
      teamMembers={teamMembers}
    />
  );

  const canLoadMoreTeamMembers =
    teamMembers.loaded &&
    isLoadMoreMembersActive &&
    !teamMembers.meta.isLastPage;

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ?
          <TeamSidePanelShow
            canLoadMoreTeamMembers={canLoadMoreTeamMembers}
            isFetchingTeamMembers={isFetchingTeamMembers}
            onLoadMoreTeamMembers={handleLoadMoreMembers}
            sidePanelIsOpen={sidePanelIsOpen}
            team={team}
            teamCompanyName={teamCompanyName}
            teamMembers={teamMembers}
          />
        : formBodyContent
      }
      closeCallback={closeCallback}
      color={sidePanelContext === 'show' ? 'secondary' : 'tertiary'}
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footerContent}
      headerContent={
        sidePanelContext === 'show' ?
          <PanelIcon Icon={TeamsIcon} scrolledAmount={scrolledAmount} />
        : <div className='popup__title popup__title--tertiary'>
            <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {sidePanelContext === 'edit' ?
                i18nTCommon('teams.editTeam')
              : i18nTCommon('teams.addNewTeam')}
            </h1>
          </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onBackdropClick}
      onOpen={() => sidePanelContext === 'new' && focusField(sidePanelFieldRef)}
      scrollHeader={sidePanelContext === 'show'}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
    />
  );
}

TeamsSidePanel.propTypes = {
  team: PropTypes.object.isRequired,
  teamCompanyName: PropTypes.string.isRequired,
  onTeamNameInputChange: PropTypes.func.isRequired,
  onTeamCompanyOptionChange: PropTypes.func.isRequired,
  onTeamMemberSelect: PropTypes.func.isRequired,
  onTeamMemberStatusChange: PropTypes.func.isRequired,
  onTeamMemberDelete: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func.isRequired,
  onUpdateClick: PropTypes.func.isRequired,
  sidePanelIsOpen: PropTypes.bool.isRequired,
  sidePanelContext: PropTypes.string.isRequired,
  closeCallback: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
};
